import { Menu, MenuItem, MenuItemLabel } from '@/components/ui/menu';
import { HStack } from '@/components/ui/hstack';
import { View } from '@/components/ui/view';
import { Pressable } from '@/components/ui/pressable';
import React, { useState } from 'react';
import i18n from 'i18n-js';
import { PanResponder } from 'react-native';
import { IconM, IconMC } from '../media/IconsV2';
import Svg, { Path, Rect, Circle, Ellipse, Line, G } from 'react-native-svg';
import { useSelector } from 'react-redux';
import _ from 'lodash';

export default function MediaDraw({ overlay, setOverlay }) {
    const [size, setSize] = useState(100);
    const [points, setPoints] = useState([]);
    const [move, setMove] = useState({ idx: -1, beg: null, end: null });
    const [tool, setTool] = useState(['cursor-move']);
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);
    const [shape, setShape] = useState(['Arrow']);
    const [stroke, setStroke] = useState(['#56CCF2']);
    const [fill, setFill] = useState(['none']);
    const [strokeWidth, setStrokeWidth] = useState(['9']);
    const enableDraw = setOverlay; 

    const colors = [
        'bg-[#CB2431]', // Dunkelrot
        'bg-[#FF6152]', // Hellrot
        'bg-[#006400]', // Dunkelgrün
        'bg-[#9AE19D]', // Hellgrün
        'bg-[#003366]', // Dunkelblau
        'bg-[#56CCF2]', // Hellblau
        'bg-[#FFD700]', // Dunkelgelb
        'bg-[#FFEB99]', // Hellgelb
        'bg-[#FF8C42]', // Orange
        'bg-[#FFB7C5]', // Rosa
        'bg-[#A0A0A0]', // Grau
        'bg-[#1C1C1E]', // Schwarz
        'bg-[#F5F5F5]' // Weiß
        // "none", // Transparent
    ];

    const strokeWidths = ['3', '6', '9', '12', '15'];

    const tools = ['apple-keyboard-shift', 'cursor-move', 'rotate-left', 'delete'];

    const arrowHead = (start, end) => {
        const r = strokeWidth[0] * 3;
        const corner = (150 * Math.PI) / 180;
        const at = Math.atan2(end.y - start.y, end.x - start.x);
        const la = at >= 0 ? at : at + 2 * Math.PI;
        const a0 = la - corner;
        const a1 = la + corner;
        return ` L ${end.x + r * Math.cos(a0)} ${end.y + r * Math.sin(a0)} L ${end.x + r * Math.cos(a1)} ${end.y + r * Math.sin(a1)} L ${end.x} ${end.y}`;
    };

    const points2Attrs = (points) => {
        let attrs;
        switch (shape[0]) {
            case 'Path':
                attrs = points[0]
                    ? {
                          d:
                              `M ${points[0].x} ${points[0].y}` +
                              points
                                  .slice(1)
                                  .map((point) => ` L ${point.x} ${point.y}`)
                                  .join('') +
                              (points.length < 2 || fill[0] == 'none' ? '' : ' Z'),
                          stroke: stroke[0],
                          strokeWidth: strokeWidth[0],
                          fill: fill[0],
                          strokeLinecap: 'round',
                          strokeLinejoin: 'round'
                      }
                    : {};
                break;
            case 'Circle':
                attrs = { cx: points[0].x, cy: points[0].y, r: Math.abs(points[1]?.x - points[0].x), stroke: stroke[0], strokeWidth: strokeWidth[0], fill: fill[0] };
                break;
            case 'Ellipse':
                attrs = { cx: points[0].x, cy: points[0].y, rx: Math.abs(points[1].x - points[0].x), ry: Math.abs(points[1].y - points[0].y), stroke: stroke[0], strokeWidth: strokeWidth[0], fill: fill[0] };
                break;
            case 'Rect':
                attrs = { x: Math.min(points[0].x, points[1].x), y: Math.min(points[0].y, points[1].y), width: Math.abs(points[1].x - points[0].x), height: Math.abs(points[1].y - points[0].y), stroke: stroke[0], strokeWidth: strokeWidth[0], fill: fill[0], strokeLinecap: 'round', strokeLinejoin: 'round' };
                break;
            case 'Arrow':
                attrs = { d: `M ${points[0].x} ${points[0].y} L ${points[1].x} ${points[1].y} ${arrowHead(points[0], points[1])}`, stroke: stroke[0], strokeWidth: strokeWidth[0], fill: stroke[0], strokeLinecap: 'round', strokeLinejoin: 'round' };
                break;
            default:
                attrs = { x1: points[0].x, y1: points[0].y, x2: points[1].x, y2: points[1].y, stroke: stroke[0], strokeWidth: strokeWidth[0], fill: fill[0], strokeLinecap: 'round', strokeLinejoin: 'round' };
        }
        return attrs;
    };

    const addPoint = (evt, gestureState) => {
        // check for element move, rotate, delete
        if (evt.nativeEvent.shiftKey) {
            if (move.idx < 0) {
                const elem = evt.nativeEvent.target;
                const idx = elem.getAttribute('idx');
                if (idx != undefined) {
                    setMove((prev) => ({ ...prev, idx: idx, beg: { x: (evt.nativeEvent.locationX * 800) / size, y: (evt.nativeEvent.locationY * 800) / size } }));
                }
            } else {
                setMove((prev) => ({ ...prev, end: { x: (evt.nativeEvent.locationX * 800) / size, y: (evt.nativeEvent.locationY * 800) / size } }));
            }
            return;
        }

        if (shape[0] == 'Path') {
            setPoints((prevPoints) => [...prevPoints, { x: (evt.nativeEvent.locationX * 800) / size, y: (evt.nativeEvent.locationY * 800) / size }]);
        } else {
            setPoints((prevPoints) => {
                let newPoints = [...prevPoints];
                if (newPoints.length < 2) {
                    newPoints.push({ x: (evt.nativeEvent.locationX * 800) / size, y: (evt.nativeEvent.locationY * 800) / size });
                } else {
                    newPoints[1] = { x: (evt.nativeEvent.locationX * 800) / size, y: (evt.nativeEvent.locationY * 800) / size };
                }
                return newPoints;
            });
        }
    };

    const addElement = (evt, gestureState) => {
        if (move.idx > -1) {
            switch (tool[0]) {
                case 'cursor-move':
                    if (move.end) {
                        // save()
                        setOverlay((prevElems) => {
                            let newElems = [...prevElems];
                            let elem = newElems[move.idx];
                            newElems[move.idx].trans = { x: (elem.trans?.x || 0) + move.end.x - move.beg.x, y: (elem.trans?.y || 0) + move.end.y - move.beg.y };
                            return newElems;
                        });
                    }
                    break;
                case 'rotate':
                    break;
                case 'delete':
                    // save()
                    setOverlay((prevElems) => {
                        let newElems = [...prevElems];
                        newElems.splice(move.idx, 1);
                        console.log('XXB', move.idx, newElems);
                        return newElems;
                    });
                    break;
            }
            setMove({ idx: -1, beg: null, end: null });
            return;
        }

        if (points.length == 0) {
            return;
        }

        if (points.length == 1) {
            points.push({ x: points[0].x + 2, y: points[0].y });
        }

        if (points.length == 2 && Math.abs(points[0].x - points[1].x) < 2 && Math.abs(points[0].y - points[1].y) < 2) {
            return;
        }

        const elem = {
            tag: shape[0],
            attrs: { ...points2Attrs(points) }
        };
        // console.log('ADD', elem, points)
        // save()
        setOverlay((prevElems) => [...prevElems, elem]);
        setPoints([]);
    };

    /*
    const save = () => {
        setUndoStack(prevStack => {
            let newStack = [...prevStack]
            newStack.push(_.cloneDeep(overlay))
            return newStack
        })
    };

    const undo = () => {
        setRedoStack(prevStack => {
            let newStack = [...prevStack]
            newStack.push(_.cloneDeep(overlay))
            setUndoStack(prevStack => {
                let newStack = [...prevStack]
                setOverlay(newStack.pop())
                return newStack
            });
            return newStack
        })
    };

    const redo = () => {
        setUndoStack(prevStack => {
            let newStack = [...prevStack]
            newStack.push(_.cloneDeep(overlay))
            setRedoStack(prevStack => {
                let newStack = [...prevStack]
                setOverlay(newStack.pop())
                return newStack
            });
            return newStack
        })
    };
    */

    const undo = () => {
        setOverlay((prevElems) => {
            newElems = [...prevElems];
            const save = newElems.pop();
            setRedoStack((prevStack) => {
                newStack = [...prevStack];
                newStack.push(save);
                return newStack;
            });
            return newElems;
        });
    };

    const redo = () => {
        setRedoStack((prevStack) => {
            newStack = [...prevStack];
            const saved = newStack.pop();
            setOverlay((prevElems) => {
                newElems = [...prevElems];
                newElems.push(saved);
                return newElems;
            });
            return newStack;
        });
    };

    const panResponder = //useRef(
        PanResponder.create({
            // Ask to be the responder:
            onStartShouldSetPanResponder: (evt, gestureState) => true,
            onStartShouldSetPanResponderCapture: (evt, gestureState) => true,
            onMoveShouldSetPanResponder: (evt, gestureState) => true,
            onMoveShouldSetPanResponderCapture: (evt, gestureState) => true,

            // The gesture has started. Show visual feedback so the user knows
            // what is happening! gestureState.d{x,y} will be set to zero now
            onPanResponderGrant: addPoint,
            // The most recent move distance is gestureState.move{X,Y}
            // The accumulated gesture distance since becoming responder is gestureState.d{x,y}
            onPanResponderMove: addPoint,
            onPanResponderTerminationRequest: (evt, gestureState) => true,
            // The user has released all touches while this view is the
            // responder. This typically means a gesture has succeeded
            onPanResponderRelease: addElement,
            onPanResponderTerminate: (evt, gestureState) => {
                // Another component has become the responder, so this gesture should be cancelled
            },
            onShouldBlockNativeResponder: (evt, gestureState) => {
                // Returns whether this component should block native components from becoming the JS
                // responder. Returns true by default. Is currently only supported on android.
                return true;
            }
        });
    // ).current;

    const handlers = enableDraw ? { ...panResponder.panHandlers } : {};

    const DrawMenus = () => {
        return (
            <HStack className="absolute -top-10 right-44">
                <Menu
                    placement="bottom left"
                    trigger={(triggerProps) => {
                        return (
                            <View className="bg-primary-50 border border-primary-900 rounded-[4px] p-1 ml-1">
                                <IconMC className="text-primary-900" size={24} name="shape-outline" />
                                <Pressable {...triggerProps} className="absolute w-8 h-8" />
                            </View>
                        );
                    }}
                    selectedKeys={shape}
                    selectionMode="single">
                    {/* <Menu.Item key='Line' textValue={i18n.t('drawing.line')} >
                        <Menu.ItemLabel className="text-sm">{i18n.t('drawing.line')}</Menu.ItemLabel>
                    </Menu.Item> */}
                    <MenuItem key="Arrow" onPress={() => setShape(['Arrow'])} textValue={i18n.t('drawing.arrow')}>
                        <MenuItemLabel className="text-sm">{i18n.t('drawing.arrow')}</MenuItemLabel>
                    </MenuItem>
                    <MenuItem key="Rect"  onPress={() => setShape(['Rect'])} textValue={i18n.t('drawing.rect')}>
                        <MenuItemLabel className="text-sm">{i18n.t('drawing.rect')}</MenuItemLabel>
                    </MenuItem>
                    <MenuItem key="Ellipse" onPress={() => setShape(['Ellipse'])} textValue={i18n.t('drawing.circle')}>
                        <MenuItemLabel className="text-sm">{i18n.t('drawing.circle')}</MenuItemLabel>
                    </MenuItem>
                    <MenuItem key="Path"  onPress={() => setShape(['Path'])} textValue={i18n.t('drawing.path')}>
                        <MenuItemLabel className="text-sm">{i18n.t('drawing.path')}</MenuItemLabel>
                    </MenuItem>
                </Menu>
                <Menu
                    placement="bottom left"
                    trigger={(triggerProps) => {
                        return (
                            <View className="bg-primary-50 border border-primary-900 rounded-[4px] p-1 ml-1">
                                <IconM className="text-primary-900" size={24} name="palette" />
                                <Pressable {...triggerProps} className="absolute w-8 h-8" />
                            </View>
                        );
                    }}
                    selectedKeys={stroke}
                    selectionMode="single">
                    {colors.map((color, c) => (
                        <MenuItem key={color.substring(4, 11)} onPress={() => setStroke([color.substring(4, 11)])} textValue={i18n.t('drawing.color' + c)}>
                            <View className={` ${color} w-[20px] h-[20px] mr-[5px] border border-primary-900 rounded-[2px] `} />
                            <MenuItemLabel className="text-sm">{i18n.t('drawing.color' + c)}</MenuItemLabel>
                        </MenuItem>
                    ))}
                </Menu>
                <Menu
                    placement="bottom left"
                    trigger={(triggerProps) => {
                        return (
                            <View className="bg-primary-50 border border-primary-900 rounded-[4px] p-1 ml-1">
                                <IconM className="text-primary-900" size={24} name="line-weight" />
                                <Pressable {...triggerProps} className="absolute w-8 h-8" />
                            </View>
                        );
                    }}
                    selectedKeys={strokeWidth}
                    selectionMode="single">
                    {strokeWidths.map((width) => (
                        <MenuItem key={width} onPress={() => setStrokeWidth([width])} textValue={width}>
                            <Svg width={100} height={20} viewBox="0 0 100 20" strokeWidth={width} stroke="black">
                                <Line x1="0" y1="10" x2="100" y2="10" />
                            </Svg>
                        </MenuItem>
                    ))}
                </Menu>
                {/* <Menu placement='bottom left' trigger={triggerProps => {
                    return (
                        <View className="bg-primary-50 border border-primary-900 rounded-[4px] p-1 ml-1">
                            <IconMC className=''text-primary-900' name='tools' />
                            <Pressable {...triggerProps} className="absolute w-8 h-8" />
                        </View>
                    )
                }} selectedKeys={tool} disabledKeys={['apple-keyboard-shift', 'rotate-left']} selectionMode='single'>
                    {tools.map(tool =>
                        <Menu.Item key={tool} onPress={() => setTool([tool])} textValue={tool} >
                            <IconMC className=''text-primary-900' name={tool} />
                            <Text lassName='text-lg text-primary-900 ml-1' >{i18n.t('drawing.' + tool)}</Text>
                        </Menu.Item>)}
                </Menu> */}
                <View className="bg-primary-50 border border-primary-900 rounded-[4px] p-1 ml-1">
                    <IconMC className={`${overlay.length == 0 ? 'text-secondary-400' : 'text-primary-900'}`} size={24} name="undo" />
                    <Pressable disabled={overlay.length == 0} onPress={() => undo()} className="absolute w-8 h-8" />
                </View>
                <View className="bg-primary-50 border border-primary-900 rounded-[4px] p-1 ml-1">
                    <IconMC className={`${redoStack.length == 0 ? 'text-secondary-400' : 'text-primary-900'}`} size={24} name="redo" />
                    <Pressable disabled={redoStack.length == 0} onPress={() => redo()} className="absolute w-8 h-8" />
                </View>
            </HStack>
        );
    };

    const Item = ({ idx, shape, attrs, trans }) => {
        let item;
        switch (shape) {
            case 'Path':
                item = <Path idx={idx} {...attrs} />;
                break;
            case 'Circle':
                item = <Circle idx={idx} {...attrs} />;
                break;
            case 'Ellipse':
                item = <Ellipse idx={idx} {...attrs} />;
                break;
            case 'Rect':
                item = <Rect idx={idx} {...attrs} />;
                break;
            case 'Arrow':
                item = <Path idx={idx} {...attrs} />;
                break;
            default:
                item = <Line idx={idx} {...attrs} />;
        }

        return trans ? <G transform={`translate(${trans.x}, ${trans.y}) rotate(${trans.r || 0})`}>{item}</G> : item;
    };

    return (
        <>
            {enableDraw && <DrawMenus />}
            <View {...handlers} className="absolute h-full w-full" onLayout={(e) => setSize(e.nativeEvent.layout.height)}>
                <Svg height={size} width={size} viewBox={`0 0 800 800`} shapeRendering="crispEdges" strokeWidth="3" xmlns="http://www.w3.org/2000/svg">
                    <Rect x="0" y="0" width={800} height={800} fill="#0000FF00" />
                    {overlay.map((elem, e) => (
                        <Item key={'OV' + e} idx={e} shape={elem.tag} attrs={elem.attrs} trans={move && move?.idx == e && move.end ? { x: (elem.trans?.x || 0) + move.end.x - move.beg.x, y: (elem.trans?.y || 0) + move.end.y - move.beg.y } : elem.trans} />
                    ))}
                    {enableDraw && (shape[0] == 'Path' || points.length > 1) && <Item idx={99999} shape={shape[0]} attrs={points2Attrs(points)} />}
                </Svg>
            </View>
        </>
    );
}
